import type { SatisDonemi } from './donem';

/**
 * S295 (kullanici 2026-09-23): Ciro trendinde gun / hafta / ay. Veri zaten GUNLUK satir (990040); hafta ve ay
 * istemcide toplanir, yeni sorgu yok. Kural Yerlice'nin tahsilat grafigindeki `cubukBoyu` ile ayni: adi konmus
 * donem kendi kuralini tasir, gun sayisi YALNIZ Ozel'de. Fark: Aylik'ta varsayilan GUN (Gizova'nin bugunku gorunumu).
 * Anlamsiz secenek (ornegin yilda 266 gunluk cubuk yaninda "Gun") hic gosterilmez.
 */
export type CubukBoyu = 'gun' | 'hafta' | 'ay';

export const CUBUK_ETIKET: Record<CubukBoyu, string> = { gun: 'Gün', hafta: 'Hafta', ay: 'Ay' };

export interface CubukKurali {
  secenekler: CubukBoyu[];
  varsayilan: CubukBoyu;
}

export interface Aralik {
  bas: string; // YYYY-MM-DD
  bit: string; // YYYY-MM-DD
}

/** Grafige giden hafta/ay cubugu. Gun boyu bu dosyadan GECMEZ: ZamanBlok'un bugunku gunluk serisi aynen kalir. */
export interface TrendCubugu {
  etiket: string;
  ciro: number;
}

const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const GUN_MS = 86_400_000;

// Hesap bastan sona UTC gece yarisinda: `toISOString` gun kaydirmaz.
const utc = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const isoEkle = (iso: string, n: number) => new Date(utc(iso) + n * GUN_MS).toISOString().slice(0, 10);

/** Araligin gun sayisi, iki uc DAHIL. */
export function gunSayisi({ bas, bit }: Aralik): number {
  return Math.round((utc(bit) - utc(bas)) / GUN_MS) + 1;
}

export function cubukBoyu(donem: SatisDonemi, aralik: Aralik): CubukKurali {
  if (donem === 'gunluk' || donem === 'dun' || donem === 'hafta') return { secenekler: ['gun'], varsayilan: 'gun' };
  if (donem === 'aylik') return { secenekler: ['gun', 'hafta'], varsayilan: 'gun' };
  if (donem === 'yillik') return { secenekler: ['hafta', 'ay'], varsayilan: 'ay' };
  const n = gunSayisi(aralik);
  if (n <= 14) return { secenekler: ['gun'], varsayilan: 'gun' };
  if (n <= 62) return { secenekler: ['gun', 'hafta'], varsayilan: 'gun' };
  return { secenekler: ['hafta', 'ay'], varsayilan: 'ay' };
}

/** Haftanin pazartesisi (donem.ts'deki "Bu hafta" ile ayni TR kurali). */
function haftaBasi(iso: string): string {
  const gun = new Date(utc(iso)).getUTCDay(); // 0 = Pazar
  return isoEkle(iso, -((gun + 6) % 7));
}

const gunAy = (iso: string) => `${Number(iso.slice(8, 10))} ${AY_KISA[Number(iso.slice(5, 7)) - 1]}`;

function kovaEtiketi(boy: 'hafta' | 'ay', ilk: string, son: string): string {
  if (boy === 'ay') return `${AY_KISA[Number(ilk.slice(5, 7)) - 1]} ${ilk.slice(0, 4)}`;
  if (ilk === son) return gunAy(ilk);
  if (ilk.slice(0, 7) === son.slice(0, 7)) return `${Number(ilk.slice(8, 10))}-${gunAy(son)}`;
  return `${gunAy(ilk)}-${gunAy(son)}`;
}

const iki = (n: number) => Math.round(n * 100) / 100;

/**
 * Gunluk satirlari haftaya ya da aya kovalar. Araligin HER gunu dolasilir (veri gelmeyen gun kovayi bolmez),
 * aralik disindaki satir yok sayilir. Hafta kovasi aralik uclarinda kirpilir ve etiketi GERCEK gunleri yazar
 * ("1-6 Eyl"). Gun boyu burada YOK: onu kovalamak veri gelmeyen gunleri 0 cubuk yapar ve ortalamayi degistirirdi.
 * 🔴 HIC SATIRI OLMAYAN KOVA DUSER (gun boyundaki kuralla ayni): Gizova'nin perakende verisi 2026-03-11'de
 * basliyor; Yillik'ta Ocak ve Subat 0 cubuk olarak cizilip aylik ortalamayi 9'a bolerek dusuruyordu (tarayicida
 * olculdu). Satiri olup cirosu 0 olan kova KALIR: o gercek bir sifirdir.
 */
export function kovala(gunler: ReadonlyArray<{ tarih: string; ciro: number }>, aralik: Aralik, boy: 'hafta' | 'ay'): TrendCubugu[] {
  const harita = new Map<string, number>();
  for (const g of gunler) harita.set(g.tarih, (harita.get(g.tarih) ?? 0) + g.ciro);
  const kovalar: Array<{ ilk: string; son: string; ciro: number; satirVar: boolean }> = [];
  let anahtar = '';
  for (let gun = aralik.bas; gun <= aralik.bit; gun = isoEkle(gun, 1)) {
    const a = boy === 'hafta' ? haftaBasi(gun) : gun.slice(0, 7);
    if (a !== anahtar) {
      kovalar.push({ ilk: gun, son: gun, ciro: 0, satirVar: false });
      anahtar = a;
    }
    const k = kovalar[kovalar.length - 1];
    k.son = gun;
    const c = harita.get(gun);
    if (c !== undefined) {
      k.ciro += c;
      k.satirVar = true;
    }
  }
  return kovalar.filter((k) => k.satirVar).map((k) => ({ etiket: kovaEtiketi(boy, k.ilk, k.son), ciro: iki(k.ciro) }));
}
